// "Sign in or sign up": one field, one path for new and returning guests.
//
// Owner-approved concept 31 (rift lab, `SignInSheet`). Step one offers Google
// and Apple, then one email field. Step two is six digit boxes for the code we
// email. The emailed code signs in an existing guest and creates a new one
// (better-auth emailOTP, `type: "sign-in"`, same as the mobile app), so there
// is no separate sign-up screen and no password on this surface.
//
// Hosts: the /sign-in page (via SignInFlow), SignInModal (header, and the
// heart on an activity page with its own heading), and the thank-you page's
// "Keep this booking" card. The host owns the surface; this owns the steps.
//
// A third step, the first name, appears only for an account the code has just
// created with no name (better-auth emailOTP creates it with `name: ""`).
import { useContext, useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { QueryClientContext } from "@tanstack/react-query";
import { flushSync } from "react-dom";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { Trans, useTranslation } from "react-i18next";
import { PW, pewterField, pewterPrimary } from "@majourneys/booking";
import { Icon } from "@majourneys/icons";
import { authApi, appleAuthEnabled, googleAuthEnabled } from "../api/auth.api";
import { sanitizeRedirectPath } from "../utils/redirect";
import { authClient } from "../../../lib/auth";
import { profileApi } from "../../account/api/profile.api";
import { accountKeys } from "../../account/constants/query-keys";
import { clearWelcome, greetingName, isNewAccount, markWelcome } from "../lib/post-sign-in";

const RESEND_COOLDOWN_S = 30;
const CODE_LENGTH = 6;
const EMPTY_CODE = Array.from({ length: CODE_LENGTH }, () => "");
const ERROR_RED = "#9B2C2C";
/** How long the code step waits before offering the spam folder and a way out. */
export const STUCK_AFTER_MS = 30_000;

export interface SignInFormProps {
  /** Called after a successful sign-in instead of navigating to the redirect path (modal usage: close and stay). */
  onSignedIn?: () => void;
  /** Overrides the redirect target for social OAuth callbacks and the post-sign-in navigation (modal usage: current page). */
  redirectPath?: string;
  /** Prefills the email step (thank-you card: the address the booking was made with). */
  initialEmail?: string;
  /** Replaces "Sign in or sign up" and its line, when the host has a reason to ask. */
  heading?: string;
  subheading?: string;
  /** Rendered above the heading on the email step (e.g. the item being saved). */
  lead?: ReactNode;
  /** Called once, right before a code is sent or a provider is opened. */
  onStart?: () => void;
  /** False on surfaces that sit inside a page (no focus jump on load). */
  autoFocus?: boolean;
  /**
   * "compact": the email step as one row (address + Save) with small Google
   * and Apple buttons under it, for a card inside a page (the thank-you
   * page's "Don't lose this booking"). The code and name steps are the same.
   *
   * "stub": the booking ticket's tear-off stub. The address is already known
   * (the booking's), so the email step is one "Email me a code" button with
   * the address under it; "Wrong email?" on the code step falls back to the
   * compact row so a typo can be fixed.
   */
  variant?: "full" | "compact" | "stub";
  /** Compact only: shown at the end of the "or with" row (e.g. "Not now"). */
  footerEnd?: ReactNode;
}

export function SignInForm({
  onSignedIn,
  redirectPath: redirectOverride,
  initialEmail,
  heading,
  subheading,
  lead,
  onStart,
  autoFocus = true,
  variant = "full",
  footerEnd,
}: SignInFormProps) {
  const { t, i18n } = useTranslation();
  const rtl = i18n.dir() === "rtl";
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { redirect?: string };
  const redirectPath = redirectOverride ?? sanitizeRedirectPath(search.redirect) ?? "/account";

  const queryClient = useContext(QueryClientContext);
  const [step, setStep] = useState<"email" | "code" | "name">("email");
  /** Stub only: the guest asked to type a different address. */
  const [editingEmail, setEditingEmail] = useState(false);
  const [email, setEmail] = useState(initialEmail ?? "");
  const [firstName, setFirstName] = useState("");
  const [stuck, setStuck] = useState(false);
  const [codeSentAt, setCodeSentAt] = useState(0);
  const [digits, setDigits] = useState<string[]>(EMPTY_CODE);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const emailRef = useRef<HTMLInputElement>(null);
  const boxRefs = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => {
    if (resendIn <= 0) return;
    const id = setInterval(() => setResendIn((s) => s - 1), 1000);
    return () => clearInterval(id);
  }, [resendIn > 0]);

  // "No email arriving": after a while on the code step with no complete code,
  // offer the spam folder, a way back to fix the address, and a new code.
  // Restarts with every code sent.
  useEffect(() => {
    if (step !== "code") {
      setStuck(false);
      return;
    }
    setStuck(false);
    const id = window.setTimeout(() => setStuck(true), STUCK_AFTER_MS);
    return () => window.clearTimeout(id);
  }, [step, codeSentAt]);

  const emailValid = /\S+@\S+\.\S+/.test(email);
  const code = digits.join("");

  function focusBox(i: number) {
    const box = boxRefs.current[Math.max(0, Math.min(CODE_LENGTH - 1, i))];
    box?.focus({ preventScroll: true });
    box?.select();
  }

  async function sendCode() {
    setError(null);
    setLoading(true);
    try {
      const result = await authApi.sendSignInCode({ email });
      if (result?.error) {
        setError(result.error.status === 429 ? t("auth.otp.too_many") : (result.error.message ?? t("auth.otp.send_failed")));
        return false;
      }
      setResendIn(RESEND_COOLDOWN_S);
      setCodeSentAt(Date.now());
      return true;
    } catch {
      setError(t("auth.otp.send_failed"));
      return false;
    } finally {
      setLoading(false);
    }
  }

  async function verify(value: string) {
    if (!new RegExp(`^\\d{${CODE_LENGTH}}$`).test(value)) {
      setError(t("auth.otp.incomplete"));
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const result = await authApi.verifySignInCode({ email, code: value });
      if (result?.error) {
        setError(result.error.message ?? t("auth.otp.invalid"));
        setDigits(EMPTY_CODE);
        focusBox(0);
        return;
      }
      const user = (result as { data?: { user?: { name?: string | null; email?: string | null; createdAt?: string | Date } } } | undefined)
        ?.data?.user;
      // A brand-new account with no name gets one optional step. Anyone with a
      // name, and anyone who has been here before, goes straight through.
      if (user && !greetingName(user) && isNewAccount(user)) {
        setStep("name");
        return;
      }
      finish();
    } catch (err) {
      setError(err instanceof Error ? err.message : t("auth.otp.invalid"));
    } finally {
      setLoading(false);
    }
  }

  /** Signed in: leave the greeting for the page we land on, then hand back. */
  function finish() {
    markWelcome("email");
    if (onSignedIn) onSignedIn();
    // `href`, not `to`: a `to` drops the query and hash of the page they came from.
    else navigate({ href: redirectPath });
  }

  async function saveFirstName() {
    const value = firstName.trim();
    if (!value) return finish();
    setError(null);
    setLoading(true);
    try {
      await profileApi.updateNames({ firstName: value });
      // The session keeps a short cookie cache of the user; ask past it so the
      // header and the booking forms see the new name without a reload.
      try {
        await authClient.getSession({ query: { disableCookieCache: true } });
        authClient.$store.notify("$sessionSignal");
      } catch {
        // The name is saved; the header catches up on the next load.
      }
      void queryClient?.invalidateQueries({ queryKey: accountKeys.profile() });
      finish();
    } catch {
      setError(t("auth.one_field.name_failed"));
    } finally {
      setLoading(false);
    }
  }

  async function provider(kind: "google" | "apple") {
    setError(null);
    setLoading(true);
    onStart?.();
    // Written before we leave: the page we come back to shows the greeting.
    markWelcome(kind);
    try {
      if (kind === "google") await authApi.signInWithGoogle({ callbackPath: redirectPath });
      else await authApi.signInWithApple({ callbackPath: redirectPath });
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t(kind === "google" ? "auth.google_sign_in_failed" : "auth.apple_sign_in_failed"),
      );
      clearWelcome();
      setLoading(false);
    }
  }

  /**
   * Writes digits into the boxes from `start`. A full code (paste, or the
   * iOS/Android one-time-code suggestion, which lands in whichever box has
   * focus) always fills from the first box.
   */
  function fill(start: number, raw: string) {
    const incoming = raw.replace(/\D/g, "");
    if (!incoming) return;
    const from = incoming.length >= CODE_LENGTH ? 0 : start;
    const next = [...digits];
    for (let k = 0; k < incoming.length && from + k < CODE_LENGTH; k++) next[from + k] = incoming[k]!;
    setDigits(next);
    const joined = next.join("");
    if (joined.length === CODE_LENGTH) {
      focusBox(CODE_LENGTH - 1);
      if (!loading) void verify(joined);
    } else {
      focusBox(Math.min(from + incoming.length, CODE_LENGTH - 1));
    }
  }

  function onBoxChange(i: number, raw: string) {
    setError(null);
    if (raw === "") {
      const next = [...digits];
      next[i] = "";
      setDigits(next);
      return;
    }
    // A box that already held a digit receives old + new: keep the new one.
    const incoming = raw.replace(/\D/g, "");
    if (incoming.length === 2 && digits[i] && incoming.includes(digits[i]!)) {
      fill(i, incoming.startsWith(digits[i]!) ? incoming.slice(1) : incoming.slice(0, 1));
      return;
    }
    fill(i, incoming);
  }

  function onBoxKeyDown(i: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Backspace" && !digits[i] && i > 0) {
      e.preventDefault();
      const next = [...digits];
      next[i - 1] = "";
      setDigits(next);
      focusBox(i - 1);
    } else if (e.key === "ArrowLeft" && i > 0) {
      e.preventDefault();
      focusBox(i - 1);
    } else if (e.key === "ArrowRight" && i < CODE_LENGTH - 1) {
      e.preventDefault();
      focusBox(i + 1);
    }
  }

  function backToEmail() {
    if (variant === "stub") setEditingEmail(true);
    setStep("email");
    setDigits(EMPTY_CODE);
    setError(null);
    window.setTimeout(() => emailRef.current?.focus(), 0);
  }

  const socials = googleAuthEnabled || appleAuthEnabled;

  async function startCode() {
    if (!emailValid || loading) return;
    onStart?.();
    if (await sendCode()) {
      // See the full email step: commit the step, then focus near the tap so
      // iOS raises the keyboard and its one-time-code suggestion.
      flushSync(() => {
        setStep("code");
        setDigits(EMPTY_CODE);
      });
      boxRefs.current[0]?.focus({ preventScroll: true });
    }
  }

  if (step === "email" && variant === "stub" && !editingEmail && emailValid) {
    return (
      <div data-testid="sign-in-stub">
        {lead}
        <div className="flex items-center gap-2.5">
          <span
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl"
            style={{ background: PW.goldTint }}
            aria-hidden="true"
          >
            <Icon name="ri-coupon-3-line" className="text-[17px]" style={{ color: PW.goldDark }} />
          </span>
          <div className="min-w-0">
            <h2 className="m-0 text-start font-heading text-[17px] font-bold leading-tight" style={{ color: PW.navy }}>
              {heading ?? t("ticket.stub_title")}
            </h2>
            <p className="m-0 mt-0.5 text-balance text-start text-[13px] font-medium leading-snug" style={{ color: PW.muted }}>
              {subheading ?? t("ticket.stub_sub")}
            </p>
          </div>
        </div>
        <PrimaryButton type="button" disabled={loading} onClick={() => void startCode()}>
          <span className="inline-flex items-center justify-center gap-2">
            <Icon name="ri-mail-line" className="text-[16px]" aria-hidden="true" />
            {loading ? t("auth.otp.sending") : t("ticket.send_code")}
          </span>
        </PrimaryButton>
        <p className="m-0 mt-1.5 text-center text-[12.5px] font-medium" style={{ color: PW.muted }}>
          {t("ticket.send_to")}{" "}
          <span className="break-all font-bold" style={{ color: PW.navy }} dir="ltr">
            {email}
          </span>
        </p>
        {error ? <ErrorLine>{error}</ErrorLine> : null}
        {socials ? (
          <div className="mt-3 grid grid-cols-2 gap-2">
            {appleAuthEnabled ? (
              <SocialButton half tone="apple" icon={<AppleMark />} disabled={loading} onClick={() => void provider("apple")}>
                Apple
              </SocialButton>
            ) : null}
            {googleAuthEnabled ? (
              <SocialButton half tone="google" icon={<GoogleMark />} disabled={loading} onClick={() => void provider("google")}>
                Google
              </SocialButton>
            ) : null}
          </div>
        ) : null}
        {footerEnd ? <div className="mt-2 flex justify-center">{footerEnd}</div> : null}
      </div>
    );
  }

  if (step === "email" && (variant === "compact" || variant === "stub")) {
    return (
      <form
        noValidate
        onSubmit={async (e) => {
          e.preventDefault();
          if (!emailValid || loading) return;
          onStart?.();
          if (await sendCode()) {
            flushSync(() => {
              setStep("code");
              setDigits(EMPTY_CODE);
            });
            boxRefs.current[0]?.focus({ preventScroll: true });
          }
        }}
      >
        {lead}
        <h2 className="m-0 text-start font-heading text-[18px] font-bold leading-tight tracking-[-0.01em]" style={{ color: PW.navy }}>
          {heading ?? t("auth.one_field.title")}
        </h2>
        <p className="m-0 mt-1 text-start text-[13.5px]" style={{ color: PW.muted }}>
          {subheading ?? t("auth.one_field.sub")}
        </p>
        <div
          className="mt-3 flex min-w-0 items-center gap-2 rounded-full py-1 pe-1 ps-4 transition-shadow focus-within:!shadow-[0_0_0_1.5px_#110E39]"
          style={pewterField(999)}
          dir="ltr"
        >
          <Icon name="ri-mail-line" className="shrink-0 text-[16px]" style={{ color: PW.muted }} aria-hidden />
          <input
            ref={emailRef}
            type="email"
            name="email"
            autoComplete="email"
            autoFocus={autoFocus}
            aria-label={t("auth.email")}
            placeholder={t("auth.email_placeholder")}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="min-w-0 flex-1 bg-transparent py-2 text-[15px] font-semibold outline-none placeholder:font-normal placeholder:text-[#5a5d77]"
            style={{ color: PW.navy }}
          />
          <button
            type="submit"
            disabled={!emailValid || loading}
            className="min-h-[40px] shrink-0 rounded-full px-5 text-[14px] font-semibold disabled:opacity-60"
            style={pewterPrimary}
          >
            {loading ? t("auth.otp.sending") : t("auth.keep_booking.save")}
          </button>
        </div>
        {error ? <ErrorLine>{error}</ErrorLine> : null}
        <div className="mt-3 flex min-w-0 items-center gap-2">
          {socials ? (
            <>
              <span className="text-[12.5px]" style={{ color: PW.muted }}>
                {t("auth.or_with")}
              </span>
              {googleAuthEnabled ? (
                <button
                  type="button"
                  aria-label={t("auth.continue_with_google")}
                  disabled={loading}
                  onClick={() => void provider("google")}
                  className="flex h-10 w-10 items-center justify-center rounded-full"
                  style={pewterField(999)}
                >
                  <GoogleMark />
                </button>
              ) : null}
              {appleAuthEnabled ? (
                <button
                  type="button"
                  aria-label={t("auth.continue_with_apple")}
                  disabled={loading}
                  onClick={() => void provider("apple")}
                  className="flex h-10 w-10 items-center justify-center rounded-full bg-black text-white"
                >
                  <AppleMark />
                </button>
              ) : null}
            </>
          ) : null}
          {footerEnd ? <span className="ms-auto">{footerEnd}</span> : null}
        </div>
      </form>
    );
  }

  if (step === "email") {
    return (
      <form
        noValidate
        onSubmit={async (e) => {
          e.preventDefault();
          if (!emailValid || loading) return;
          onStart?.();
          if (await sendCode()) {
            // Commit the step now so the first box exists, then focus it as
            // close to the tap as possible: iOS only raises the keyboard (and
            // its one-time-code suggestion) for a focus near the gesture.
            flushSync(() => {
              setStep("code");
              setDigits(EMPTY_CODE);
            });
            boxRefs.current[0]?.focus({ preventScroll: true });
          }
        }}
      >
        {lead}
        <h2 className="m-0 text-center font-heading text-[22px] font-bold tracking-[-0.01em]" style={{ color: PW.navy }}>
          {heading ?? t("auth.one_field.title")}
        </h2>
        {/* Less text (owner, 2026-10-24): no default subtitle; a caller that
            needs one (a gate with a reason) still passes it. */}
        {subheading ? (
          <p className="m-0 mt-1 text-center text-[14px]" style={{ color: PW.muted }}>
            {subheading}
          </p>
        ) : null}

        {socials ? (
          <>
            <div className="mt-5 flex flex-col gap-2.5">
              {googleAuthEnabled ? (
                <SocialButton tone="google" icon={<GoogleMark />} disabled={loading} onClick={() => void provider("google")}>
                  {t("auth.continue_with_google")}
                </SocialButton>
              ) : null}
              {appleAuthEnabled ? (
                <SocialButton tone="apple" icon={<AppleMark />} disabled={loading} onClick={() => void provider("apple")}>
                  {t("auth.continue_with_apple")}
                </SocialButton>
              ) : null}
            </div>
            <div className="my-5 flex items-center gap-3 text-[13px]" style={{ color: PW.muted }}>
              <span className="h-px flex-1" style={{ background: PW.hairline }} aria-hidden="true" />
              {t("auth.or")}
              <span className="h-px flex-1" style={{ background: PW.hairline }} aria-hidden="true" />
            </div>
          </>
        ) : (
          <div className="mt-5" />
        )}

        <label className="block rounded-[16px] px-4 pb-2 pt-2 transition-shadow focus-within:!shadow-[0_0_0_1.5px_#110E39]" style={pewterField(16)}>
          <span className="block text-[12px] font-semibold" style={{ color: PW.muted }}>
            {t("auth.email")}
          </span>
          <input
            ref={emailRef}
            type="email"
            name="email"
            autoComplete="email"
            autoFocus={autoFocus}
            dir="ltr"
            placeholder={t("auth.email_placeholder")}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="block w-full bg-transparent text-[16px] outline-none placeholder:text-[#5a5d77]"
            style={{ color: PW.navy, textAlign: rtl ? "right" : "left" }}
          />
        </label>

        {error ? <ErrorLine>{error}</ErrorLine> : null}

        <PrimaryButton type="submit" disabled={!emailValid || loading}>
          {loading ? t("auth.otp.sending") : t("auth.continue")}
        </PrimaryButton>

        <p className="m-0 mt-3 text-center text-[12.5px] [text-wrap:balance]" style={{ color: PW.muted }}>
          {t("auth.one_field.no_password")}
        </p>
      </form>
    );
  }

  if (step === "name") {
    return (
      <form
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          if (!loading) void saveFirstName();
        }}
      >
        <h2 className="m-0 text-center font-heading text-[22px] font-bold tracking-[-0.01em]" style={{ color: PW.navy }}>
          {t("auth.welcome_to")}
        </h2>
        <p className="m-0 mt-1 text-center text-[14px]" style={{ color: PW.muted }}>
          {t("auth.one_field.name_sub")}
        </p>
        <label
          className="mt-5 block rounded-[16px] px-4 pb-2 pt-2 transition-shadow focus-within:!shadow-[0_0_0_1.5px_#110E39]"
          style={pewterField(16)}
        >
          <span className="block text-[12px] font-semibold" style={{ color: PW.muted }}>
            {t("auth.first_name")}
          </span>
          <input
            type="text"
            name="given-name"
            autoComplete="given-name"
            autoFocus
            maxLength={60}
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            className="block w-full bg-transparent text-[16px] outline-none"
            style={{ color: PW.navy }}
          />
        </label>
        {error ? <ErrorLine>{error}</ErrorLine> : null}
        <PrimaryButton type="submit" disabled={!firstName.trim() || loading}>
          {t("auth.one_field.done")}
        </PrimaryButton>
        <div className="mt-3 text-center">
          <button
            type="button"
            onClick={finish}
            disabled={loading}
            className="min-h-6 px-2 text-[13.5px] font-semibold underline underline-offset-4 disabled:opacity-40"
            style={{ color: PW.navy }}
          >
            {t("auth.one_field.skip")}
          </button>
        </div>
      </form>
    );
  }

  const showStuck = stuck && code.length < CODE_LENGTH && !loading;

  return (
    <form
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        void verify(code);
      }}
    >
      <button
        type="button"
        onClick={backToEmail}
        aria-label={t("auth.back")}
        className="flex h-9 w-9 items-center justify-center rounded-full transition-colors hover:bg-[rgba(17,14,57,0.04)]"
        style={{ background: PW.controlFill, color: PW.navy, border: `1.5px solid ${PW.controlEdge}` }}
      >
        <Icon name={rtl ? "ri-arrow-right-s-line" : "ri-arrow-left-s-line"} className="text-[18px]" aria-hidden="true" />
      </button>
      <h2 className="m-0 mt-3 font-heading text-[22px] font-bold tracking-[-0.01em]" style={{ color: PW.navy }}>
        {t("auth.otp.code_title")}
      </h2>
      <p className="m-0 mt-1 break-words text-[14px]" style={{ color: PW.muted }}>
        <Trans
          i18nKey="auth.one_field.code_sub"
          values={{ email }}
          components={{ email: <bdi dir="ltr" className="font-semibold" style={{ color: PW.navy }} /> }}
        />
      </p>

      <div role="group" aria-label={t("auth.code_label")} className="mt-5 grid grid-cols-6 gap-2" dir="ltr">
        {digits.map((d, i) => (
          <input
            key={i}
            ref={(el) => {
              boxRefs.current[i] = el;
            }}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete="one-time-code"
            aria-label={t("auth.one_field.digit_label", { n: i + 1, total: CODE_LENGTH })}
            value={d}
            onChange={(e) => onBoxChange(i, e.target.value)}
            onKeyDown={(e) => onBoxKeyDown(i, e)}
            onFocus={(e) => e.currentTarget.select()}
            onPaste={(e) => {
              e.preventDefault();
              fill(i, e.clipboardData.getData("text"));
            }}
            className="h-14 w-full min-w-0 rounded-[14px] text-center text-[22px] font-bold tabular-nums caret-[#110E39] outline-none transition-shadow focus:!shadow-[0_0_0_1.5px_#110E39]"
            style={boxStyle(!!error)}
          />
        ))}
      </div>

      {error ? <ErrorLine>{error}</ErrorLine> : null}

      <PrimaryButton type="submit" disabled={code.length !== CODE_LENGTH || loading}>
        {loading ? t("auth.otp.verifying") : t("auth.continue")}
      </PrimaryButton>

      {showStuck ? (
        <div role="status" className="mt-4 rounded-[14px] p-3.5 text-[13.5px]" style={{ background: PW.softTint, color: PW.navy }}>
          {t("auth.otp.spam_hint")}
          <div className="mt-2 flex flex-wrap items-center justify-between gap-2 font-semibold">
            <button
              type="button"
              onClick={backToEmail}
              className="min-h-6 underline underline-offset-4"
              style={{ color: PW.navy }}
            >
              {t("auth.otp.wrong_email")}
            </button>
            <ResendControl resendIn={resendIn} loading={loading} onResend={() => void sendCode()} />
          </div>
        </div>
      ) : (
        <div className="mt-4 text-center text-[13.5px]" style={{ color: PW.muted }}>
          <ResendControl resendIn={resendIn} loading={loading} onResend={() => void sendCode()} />
        </div>
      )}
    </form>
  );
}

/* ------------------------------------------------------------ pieces */

function ResendControl({ resendIn, loading, onResend }: { resendIn: number; loading: boolean; onResend: () => void }) {
  const { t } = useTranslation();
  if (resendIn > 0) {
    return (
      <span aria-live="polite" style={{ color: PW.muted }} className="font-normal">
        {t("auth.one_field.resend_in", { seconds: resendIn })}
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={onResend}
      disabled={loading}
      className="min-h-6 font-semibold underline underline-offset-4 disabled:opacity-40"
      style={{ color: PW.navy }}
    >
      {t("auth.one_field.resend")}
    </button>
  );
}

function boxStyle(invalid: boolean): CSSProperties {
  return {
    ...pewterField(14, invalid ? { hairline: "rgba(155,44,44,0.75)" } : {}),
    color: PW.navy,
  };
}

function PrimaryButton({ children, ...props }: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      {...props}
      className="mt-4 h-12 w-full rounded-full text-[15px] font-semibold transition-[opacity,transform] duration-150 active:scale-[0.985] disabled:opacity-35"
      style={pewterPrimary}
    >
      {children}
    </button>
  );
}

/**
 * Brand buttons follow each provider's own rules rather than the pewter skin:
 * Apple's is black with the white Apple mark, Google's is white with the
 * four-colour G. Label first, mark at the start, both the same size.
 */
function SocialButton({
  tone,
  icon,
  half = false,
  children,
  ...props
}: { tone: "apple" | "google"; icon: ReactNode; half?: boolean } & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const apple = tone === "apple";
  // `half`: two side by side (the ticket stub). The mark sits beside the
  // label instead of pinned to the start, or "Google" has no room.
  if (half) {
    return (
      <button
        type="button"
        {...props}
        className="flex h-11 w-full min-w-0 items-center justify-center gap-2 rounded-full px-3 text-[14px] font-semibold transition-[transform,opacity] duration-150 active:scale-[0.985] disabled:opacity-50"
        style={
          apple
            ? { background: "#000", color: "#fff" }
            : { background: "#fff", color: "#1F1F1F", boxShadow: `0 0 0 1px ${PW.controlEdge}` }
        }
      >
        <span className="flex h-5 w-5 shrink-0 items-center justify-center" aria-hidden="true">
          {icon}
        </span>
        {children}
      </button>
    );
  }
  return (
    <button
      type="button"
      {...props}
      className="relative flex h-12 w-full items-center justify-center whitespace-nowrap rounded-full px-10 text-[14px] font-semibold transition-[transform,opacity] min-[360px]:px-12 min-[360px]:text-[15px] duration-150 active:scale-[0.985] disabled:opacity-50"
      style={
        apple
          ? { background: "#000", color: "#fff" }
          : { background: "#fff", color: "#1F1F1F", boxShadow: `0 0 0 1px ${PW.controlEdge}` }
      }
    >
      <span className="absolute start-4 flex h-5 w-5 items-center justify-center min-[360px]:start-5" aria-hidden="true">
        {icon}
      </span>
      {/* One line at 320 in every locale ("Continuer avec Google" wrapped
          its last word): tighter padding and 14px below 360. */}
      {children}
    </button>
  );
}

function ErrorLine({ children }: { children: ReactNode }) {
  return (
    <p role="alert" className="m-0 mt-3 flex items-start gap-1.5 text-[13.5px]" style={{ color: ERROR_RED }}>
      <Icon name="ri-error-warning-line" className="mt-[1px] text-[16px]" aria-hidden="true" />
      <span>{children}</span>
    </p>
  );
}

function AppleMark() {
  return (
    <svg viewBox="0 0 384 512" width="17" height="19" fill="currentColor" aria-hidden="true">
      <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.3-19.7-76.4-19.7C71.5 141.2 16 184.5 16 272.4c0 26 4.8 52.9 14.3 80.6 12.7 36.3 58.6 125.4 106.5 123.9 25-.6 42.7-17.8 75.3-17.8 31.6 0 48 17.8 76.4 17.8 48.3-.7 89.9-81.7 102-118.1-64.7-30.5-61.8-89.4-61.8-90.1zM256.8 84.6c30.9-36.6 28.1-69.9 27.2-81.9-27.3 1.6-58.9 18.6-76.9 39.6-19.9 22.5-31.6 50.3-29.1 79.1 29.5 2.3 56.5-12.9 78.8-36.8z" />
    </svg>
  );
}

function GoogleMark() {
  return (
    <svg viewBox="0 0 24 24" width="19" height="19" aria-hidden="true">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.76h3.56c2.08-1.92 3.28-4.74 3.28-8.09z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.56-2.76c-.98.66-2.23 1.06-3.72 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.11a6.6 6.6 0 0 1 0-4.22V7.05H2.18a11 11 0 0 0 0 9.9l3.66-2.84z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.05l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
    </svg>
  );
}
